import { Component, Input } from '@angular/core';
import { ProjectAccess, ProjectAccessAssignmentMask } from '@completion/models';
import { RoleLike, ProjectRoleMatrixVm } from './project-role-matrix.service';

@Component({
  selector: 'app-project-role-matrix',
  templateUrl: './project-role-matrix.component.html',
  styleUrls: ['./project-role-matrix.component.scss'],
  standalone: false,
})
export class ProjectRoleMatrixComponent<R extends RoleLike> {
  @Input() vm: ProjectRoleMatrixVm<R> | null = null;
  @Input() hasRole!: (access: ProjectAccess, role: R, masks: ProjectAccessAssignmentMask[] | null | undefined) => boolean;
  @Input() getRoleIcon!: (access: ProjectAccess, role: R, masks: ProjectAccessAssignmentMask[] | null | undefined) => string;
  @Input() toggleRole!: (access: ProjectAccess, role: R, masks: ProjectAccessAssignmentMask[] | null | undefined) => void;

  hoveredRow: ProjectAccess | null = null;
  hoveredColumn: string | null = null;

  /** null = no filter, true = show only rows WITH the role, false = show only rows WITHOUT */
  private columnFilters = new Map<string, boolean | null>();

  get filteredRows(): ProjectAccess[] {
    const vm = this.vm;
    if (!vm) return [];
    const activeFilters = [...this.columnFilters.entries()].filter(([, v]) => v !== null);
    if (!activeFilters.length) return vm.projectAccessList ?? [];

    return (vm.projectAccessList ?? []).filter((pa) =>
      activeFilters.every(([col, wantsRole]) => {
        const role = vm.roles.find((r) => this.roleToColumn(r) === col);
        if (!role) return true;
        const has = this.hasRole(pa, role, vm.masks);
        return wantsRole ? has : !has;
      }),
    );
  }

  cycleFilter(columnId: string, event: Event): void {
    event.stopPropagation();
    const current = this.columnFilters.get(columnId) ?? null;
    // Cycle: null → true → false → null
    const next = current === null ? true : current === true ? false : null;
    this.columnFilters = new Map(this.columnFilters).set(columnId, next);
  }

  getFilterState(columnId: string): boolean | null {
    return this.columnFilters.get(columnId) ?? null;
  }

  getFilterIcon(columnId: string): string {
    const s = this.getFilterState(columnId);
    if (s === true) return 'check_circle';
    if (s === false) return 'cancel';
    return 'filter_list';
  }

  getFilterTooltip(columnId: string): string {
    const s = this.getFilterState(columnId);
    if (s === true) return 'Filter: has role — click to show without';
    if (s === false) return 'Filter: no role — click to clear';
    return 'Click to filter by role';
  }

  // Column ID used by matColumnDef and displayedColumns; must match containers
  roleToColumn(role: R): string {
    return `role-${role.id}`;
  }
}
